import { saveAs } from "file-saver";

function escapeXml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function getSvgInnerMarkup(svg: SVGSVGElement) {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.removeAttribute("width");
  clone.removeAttribute("height");
  return clone.innerHTML;
}

function getSvgViewBox(svg: SVGSVGElement) {
  const viewBox = svg.getAttribute("viewBox");
  if (viewBox) return viewBox;
  const bounds = svg.getBoundingClientRect();
  return `0 0 ${Math.max(bounds.width, 1)} ${Math.max(bounds.height, 1)}`;
}

function getEmbeddedChartMarkup(
  chart: SVGSVGElement,
  position: { x: number; y: number; width: number; height: number },
) {
  return `<svg x="${position.x}" y="${position.y}" width="${position.width}" height="${position.height}" viewBox="${escapeXml(getSvgViewBox(chart))}" preserveAspectRatio="xMidYMid meet" overflow="hidden">${getSvgInnerMarkup(chart)}</svg>`;
}

type EmbeddedLogo = {
  innerMarkup: string;
  viewBox: string;
};

async function getEmbeddedRevelateLogo(): Promise<EmbeddedLogo | null> {
  const response = await fetch("/revelate-logo.svg");
  if (!response.ok) return null;
  const source = await response.text();
  const document = new DOMParser().parseFromString(source, "image/svg+xml");
  const svg = document.documentElement;
  if (svg.localName !== "svg" || document.querySelector("parsererror")) return null;
  svg.querySelectorAll("script, foreignObject").forEach(element => element.remove());
  return {
    innerMarkup: svg.innerHTML,
    viewBox: svg.getAttribute("viewBox") ?? "0 0 132 25",
  };
}

export function getRevenueIntelligenceExportPeriodLabel(
  period?: string | null,
  language: "en" | "sv" = "en",
) {
  if (!period) return null;
  const labels: Record<string, string | null> = {
    prev_quarter: "Previous quarter",
    this_quarter: "This quarter",
    next_quarter: "Next quarter",
    next_6_months: "Next 6 months",
    this_year: null,
  };
  const label = period
    .split(" · ")
    .map((part) => {
      const normalized = part.trim();
      return normalized in labels ? labels[normalized] : normalized;
    })
    .filter((part): part is string => Boolean(part))
    .join(" · ") || null;
  if (language !== "sv" || !label) return label;
  const translations: Record<string, string> = {
    "Previous quarter": "Föregående kvartal",
    "This quarter": "Detta kvartal",
    "Next quarter": "Nästa kvartal",
    "Next 6 months": "Nästa 6 månader",
  };
  return label.split(" · ").map(part => translations[part] ?? part).join(" · ");
}

function getLogoMarkup(logo: EmbeddedLogo | null) {
  return logo
    ? `<svg x="1394" y="821" width="112" height="22" viewBox="${escapeXml(logo.viewBox)}" preserveAspectRatio="xMidYMid meet" aria-label="Revelate logo">${logo.innerMarkup}</svg>`
    : "";
}

export async function buildRevenueIntelligencePresentationSvg({
  chart,
  conclusion,
  dataAsOf,
  period,
  source,
  title,
  language = "en",
}: {
  chart: SVGSVGElement;
  conclusion?: string | null;
  dataAsOf: string;
  period?: string | null;
  source: string;
  title: string;
  language?: "en" | "sv";
}) {
  const availableWidth = 1_440;
  const availableHeight = conclusion ? 540 : 600;
  const chartY = conclusion ? 190 : 150;
  const logo = await getEmbeddedRevelateLogo();
  const logoMarkup = getLogoMarkup(logo);
  const periodLabel = getRevenueIntelligenceExportPeriodLabel(period, language);
  const dataLabel = language === "sv" ? "Data per" : "Data as of";
  const sourceLabel = language === "sv" ? "Källa" : "Source";

  return `
<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="900" viewBox="0 0 1600 900">
  <rect width="1600" height="900" fill="#FFFFFF"/>
  <text x="80" y="78" fill="#231942" font-family="Inter, Arial, sans-serif" font-size="34" font-weight="600">${escapeXml(title)}</text>
  <text x="80" y="116" fill="#6B7280" font-family="Inter, Arial, sans-serif" font-size="18">${escapeXml([periodLabel, `${dataLabel} ${dataAsOf}`].filter(Boolean).join(" · "))}</text>
  ${conclusion ? `<text x="80" y="158" fill="#374151" font-family="Inter, Arial, sans-serif" font-size="20">${escapeXml(conclusion)}</text>` : ""}
  ${getEmbeddedChartMarkup(chart, { x: 80, y: chartY, width: availableWidth, height: availableHeight })}
  <line x1="80" x2="1520" y1="800" y2="800" stroke="#E5E7EB"/>
  <text x="80" y="842" fill="#6B7280" font-family="Inter, Arial, sans-serif" font-size="15">${sourceLabel}: ${escapeXml(source)}</text>
  ${logoMarkup}
  <text x="1374" y="840" fill="#5E548E" font-family="Inter, Arial, sans-serif" font-size="14" text-anchor="end">Intelligence by Revelate</text>
</svg>`.trim();
}

function safeFileName(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80) || "revelate-analysis";
}

export async function exportRevenueIntelligenceChart({
  chart,
  conclusion,
  dataAsOf,
  format,
  period,
  source,
  title,
  language = "en",
}: {
  chart: SVGSVGElement;
  conclusion?: string | null;
  dataAsOf: string;
  format: "png" | "svg";
  period?: string | null;
  source: string;
  title: string;
  language?: "en" | "sv";
}) {
  const svg = await buildRevenueIntelligencePresentationSvg({
    chart,
    conclusion,
    dataAsOf,
    period,
    source,
    title,
    language,
  });
  const baseName = safeFileName(title);
  if (format === "svg") {
    saveAs(
      new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
      `${baseName}.svg`,
    );
    return;
  }

  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = objectUrl;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = 900;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("PNG export is not available.");
    context.drawImage(image, 0, 0, 1600, 900);
    const pngBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((value) => {
        if (value) resolve(value);
        else reject(new Error("PNG export failed."));
      }, "image/png", 1);
    });
    saveAs(pngBlob, `${baseName}.png`);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function exportRevenueIntelligenceBrief({
  charts,
  dataAsOf,
  period,
  title,
  language = "en",
}: {
  charts: Array<{
    chart: SVGSVGElement;
    conclusion?: string | null;
    source: string;
    title: string;
  }>;
  dataAsOf: string;
  period?: string | null;
  title: string;
  language?: "en" | "sv";
}) {
  const selectedCharts = charts.slice(0, 3);
  if (!selectedCharts.length) throw new Error("Brief has no charts to export.");
  const logo = await getEmbeddedRevelateLogo();
  const layout = selectedCharts.length === 1
    ? [{ x: 80, y: 178, width: 1440, height: 550 }]
    : selectedCharts.length === 2
      ? [
          { x: 80, y: 210, width: 700, height: 470 },
          { x: 820, y: 210, width: 700, height: 470 },
        ]
      : [
          { x: 80, y: 235, width: 453, height: 390 },
          { x: 573, y: 235, width: 453, height: 390 },
          { x: 1067, y: 235, width: 453, height: 390 },
        ];
  const chartMarkup = selectedCharts.map((item, index) => {
    const position = layout[index];
    const chartTop = position.y + (item.conclusion ? 80 : 54);
    const chartPosition = {
      x: position.x + 20,
      y: chartTop,
      width: position.width - 40,
      height: position.height - (chartTop - position.y) - 20,
    };
    return `
      <rect x="${position.x}" y="${position.y}" width="${position.width}" height="${position.height}" rx="12" fill="#FFFFFF" stroke="#E5E7EB"/>
      <text x="${position.x + 20}" y="${position.y + 34}" fill="#231942" font-family="Inter, Arial, sans-serif" font-size="19" font-weight="600">${escapeXml(item.title)}</text>
      ${item.conclusion ? `<text x="${position.x + 20}" y="${position.y + 60}" fill="#6B7280" font-family="Inter, Arial, sans-serif" font-size="13">${escapeXml(item.conclusion.slice(0, 90))}</text>` : ""}
      ${getEmbeddedChartMarkup(item.chart, chartPosition)}
    `;
  }).join("");
  const sourceLine = selectedCharts
    .map(item => item.source)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(" · ");
  const logoMarkup = getLogoMarkup(logo);
  const periodLabel = getRevenueIntelligenceExportPeriodLabel(period, language);
  const dataLabel = language === "sv" ? "Data per" : "Data as of";
  const analysesLabel = language === "sv"
    ? "Aktuella Revenue Intelligence-analyser"
    : "Current approved Revenue Intelligence analyses";
  const sourcesLabel = language === "sv" ? "Källor" : "Sources";
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="900" viewBox="0 0 1600 900">
  <rect width="1600" height="900" fill="#F9FAFB"/>
  <text x="80" y="74" fill="#231942" font-family="Inter, Arial, sans-serif" font-size="36" font-weight="600">${escapeXml(title)}</text>
  <text x="80" y="112" fill="#6B7280" font-family="Inter, Arial, sans-serif" font-size="18">${escapeXml([periodLabel, `${dataLabel} ${dataAsOf}`].filter(Boolean).join(" · "))}</text>
  <text x="80" y="150" fill="#374151" font-family="Inter, Arial, sans-serif" font-size="19">${analysesLabel}</text>
  ${chartMarkup}
  <line x1="80" x2="1520" y1="800" y2="800" stroke="#E5E7EB"/>
  <text x="80" y="842" fill="#6B7280" font-family="Inter, Arial, sans-serif" font-size="14">${sourcesLabel}: ${escapeXml(sourceLine)}</text>
  ${logoMarkup}
  <text x="1374" y="840" fill="#5E548E" font-family="Inter, Arial, sans-serif" font-size="14" text-anchor="end">Intelligence by Revelate</text>
</svg>`.trim();

  const objectUrl = URL.createObjectURL(
    new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
  );
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = objectUrl;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = 900;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Brief export is not available.");
    context.drawImage(image, 0, 0, 1600, 900);
    const png = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((value) => {
        if (value) resolve(value);
        else reject(new Error("Brief export failed."));
      }, "image/png", 1);
    });
    saveAs(png, `${safeFileName(title)}.png`);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
